// Skill toggle screen for one agent. Lists every tenant skill, marks which
// are currently enabled, and PUTs the new set on save.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { Card } from '@/components/primitives/Card';
import { Badge, type BadgeTone } from '@/components/primitives/Badge';
import { Btn } from '@/components/primitives/Btn';
import { Empty } from '@/components/primitives/Empty';
import { PageContainer, PageHeader, SectionLabel } from '@/components/PageHeader';
import { fetchAgent, updateAgentSkills } from '@/lib/agents-api';
import { extractApiError } from '@/lib/api-client';

const RISK_TONE: Record<string, BadgeTone> = {
  LOW: 'ok',
  MEDIUM: 'info',
  HIGH: 'warn',
  CRITICAL: 'err',
};

export function AgentSkillsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['agent', id],
    queryFn: () => fetchAgent(id as string),
    enabled: !!id,
  });

  const [enabled, setEnabled] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (query.data && enabled === null) {
      setEnabled(new Set(query.data.skills.filter((s) => s.enabled).map((s) => s.id)));
    }
  }, [query.data, enabled]);

  const mutation = useMutation({
    mutationFn: (ids: string[]) => updateAgentSkills(id as string, { enabledSkillIds: ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', id] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });

  if (query.isLoading || !enabled) {
    return (
      <PageContainer>
        <div className="text-[13px] text-text-3 py-10 text-center">Loading agent…</div>
      </PageContainer>
    );
  }
  if (query.isError || !query.data) {
    return (
      <PageContainer>
        <Card>
          <Empty title="Couldn't load agent" actions={<Btn onClick={() => query.refetch()}>Retry</Btn>} />
        </Card>
      </PageContainer>
    );
  }

  const agent = query.data;
  const enabledList = agent.skills.filter((s) => enabled.has(s.id));
  const availableList = agent.skills.filter((s) => !enabled.has(s.id));

  function toggle(skillId: string, on: boolean) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (on) next.add(skillId);
      else next.delete(skillId);
      return next;
    });
  }

  const dirty =
    enabled.size !== agent.skills.filter((s) => s.enabled).length ||
    agent.skills.some((s) => s.enabled !== enabled.has(s.id));

  const saveError = mutation.isError ? extractApiError(mutation.error) : null;

  return (
    <PageContainer>
      <div className="max-w-[860px]">
        <PageHeader
          title={`Skills — ${agent.name}`}
          description="Toggle which skills this agent can use."
          actions={
            <>
              <Btn icon={ArrowLeft} onClick={() => navigate(`/agents/${agent.id}`)}>
                Back
              </Btn>
              <Btn
                kind="primary"
                icon={Save}
                disabled={!dirty || mutation.isPending}
                onClick={() => mutation.mutate(Array.from(enabled))}
              >
                {mutation.isPending ? 'Saving…' : mutation.isSuccess && !dirty ? 'Saved' : 'Save skills'}
              </Btn>
            </>
          }
        />

        {saveError && (
          <div
            role="alert"
            className="mb-3.5 px-3 py-2 rounded-md text-[12.5px]"
            style={{ background: 'var(--err-soft)', color: 'var(--err)' }}
          >
            {saveError}
          </div>
        )}

        <SectionLabel>Enabled skills</SectionLabel>
        {enabledList.length === 0 ? (
          <Card>
            <Empty
              title="No skills enabled yet"
              body="Enable a skill from the available list below."
            />
          </Card>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {enabledList.map((s) => (
              <SkillCard
                key={s.id}
                name={s.name}
                description={s.description}
                risk={s.riskLevel}
                action={
                  <Btn size="sm" onClick={() => toggle(s.id, false)}>
                    Remove
                  </Btn>
                }
              />
            ))}
          </div>
        )}

        <SectionLabel>Available skills</SectionLabel>
        {availableList.length === 0 ? (
          <Card>
            <Empty title="Every skill is enabled." />
          </Card>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {availableList.map((s) => (
              <SkillCard
                key={s.id}
                name={s.name}
                description={s.description}
                risk={s.riskLevel}
                muted
                action={
                  <Btn size="sm" kind="primary" onClick={() => toggle(s.id, true)}>
                    Add
                  </Btn>
                }
              />
            ))}
          </div>
        )}
      </div>
    </PageContainer>
  );
}

function SkillCard({
  name,
  description,
  risk,
  muted = false,
  action,
}: {
  name: string;
  description: string;
  risk: string;
  muted?: boolean;
  action: React.ReactNode;
}) {
  return (
    <Card pad={14}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-medium text-[13.5px]" style={{ opacity: muted ? 0.85 : 1 }}>
          {name}
        </span>
        <Badge tone={RISK_TONE[risk] ?? 'default'}>
          {risk.charAt(0) + risk.slice(1).toLowerCase()} risk
        </Badge>
      </div>
      <div
        className="text-[12.5px] text-text-2 leading-snug min-h-[34px]"
        style={{ opacity: muted ? 0.85 : 1 }}
      >
        {description}
      </div>
      <div className="mt-2.5">{action}</div>
    </Card>
  );
}
